import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import ListItem from 'components/Luxkit/List/ListItem'
import ListItemPrice from 'components/Luxkit/List/ListItemPrice'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { OfferPageActions, takeOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import React, { useCallback, useContext, useMemo } from 'react'
import { getDurationOptions } from 'selectors/multipleRoomBookingSelectors'
import styled from 'styled-components'
import { OfferDurationOption } from '../../DurationTabs/OfferDurationTabs'
import { min } from 'lib/arrayUtils'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import SolidFireIcon from 'components/Luxkit/Icons/solid/SolidFireIcon'
import { rem } from 'polished'
import Pill from 'components/Luxkit/Pill'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import Label from 'components/Luxkit/Label'
import { isLPCTacticalEnabled } from 'lib/offerUtils'

const SizedDropdownContainer = styled(DropdownContainer)`
  max-height: 60vh;
`

const OptionListItem = styled(ListItem)`
  padding: ${rem(16)};
`

function getOptionLabel(t: TFunction<['common', 'offerPage']>, option: OfferDurationOption) {
  if (option.soldOut) {
    return t('SOLD OUT', { ns: 'offerPage' })
  } else if (option.lowestPrice === undefined) {
    return undefined
  } else {
    return t('From', { ns: 'common' })
  }
}

function getSubtitleLabel(t: TFunction<['common', 'offerPage']>, option: OfferDurationOption) {
  if (isLPCTacticalEnabled() && option.hasTactical) {
    return <LabelGroup>
      <Label
        startIcon={<SolidFireIcon colour="urgency" size="XS" />}
        variant="critical"
        kind="plain">
        {t('Limited Time Special', { ns: 'offerPage' })}
      </Label>
      {option?.discountPercent > 0 && <Pill kind="primary" variant="success" >-{option.discountPercent}%</Pill>}
    </LabelGroup>
  }
  return undefined
}

interface Props {
  durationOptions: Array<OfferDurationOption>;
  selectedDuration: number;
  onClose?: () => void;
  bundledWithFlightsOnly: boolean;
  currentOfferId?: string;
  bundleOfferId?: string;
  offerId?: string;
}

function OfferCalendarDurationDropdown(props: Props) {
  const { durationOptions, onClose, selectedDuration, bundledWithFlightsOnly, currentOfferId, bundleOfferId } = props
  const dispatch = useOfferPageDispatch()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const { t } = useTranslation(['common', 'hotelOffer', 'offerPage'])

  const onDurationClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const duration = parseInt(e.currentTarget.dataset.duration!, 10)
    dispatch({ type: OfferPageActions.setDuration, duration, offerId: currentOfferId })
    onClose?.()
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseSubFilter, CALENDAR_GA_EVENT_LABEL.OpenDuration)
  }, [dispatch, onClose, onUserAnalyticsAction, currentOfferId])
  const minDuration = useMemo(() => min(durationOptions, duration => duration.duration)!, [durationOptions])

  // return view based on option here

  return (
    <SizedDropdownContainer
      title={t('howLong', { ns: 'hotelOffer' })}
      subtitle={t('minStayForOffer', { ns: 'offerPage', count: minDuration.duration })}
      onClose={onClose}
      fit="container"
    >
      {durationOptions.map(option => <OptionListItem
        key={option.duration}
        title={t('night_count', { ns: 'common', count: option.duration })}
        subtitle={getSubtitleLabel(t, option)}
        selected={option.duration === selectedDuration}
        data-duration={option.duration}
        onClick={onDurationClick}
      >
        <ListItemPrice
          price={option.soldOut || bundledWithFlightsOnly || bundleOfferId ? undefined : option.lowestPrice}
          label={bundledWithFlightsOnly || bundleOfferId ? undefined : getOptionLabel(t, option)}
        />
      </OptionListItem>)}
    </SizedDropdownContainer>
  )
}

export default withOfferPageAndReduxState((state, appState, props) => {
  const currentOfferId = props.offerId ?? state.paginator?.currentOfferId
  const currentState = takeOfferState(state, currentOfferId)

  return {
    selectedDuration: currentState.duration,
    bundleOfferId: currentState.bundleOffer?.id,
    currentOfferId,
    durationOptions: getDurationOptions(currentState, appState),
    bundledWithFlightsOnly: currentState.offer?.bundledWithFlightsOnly,
  }
})(OfferCalendarDurationDropdown)
